import { getContentImageProps } from '@/lib/utils';
import Link from 'next/link';
import ExternalLink from '@/components/elements/ExternalLink';
import { resolveImageAltText } from '@/lib/utils';

export default async function FullImage({ block, position, entry_uid }) {
  const {
    preheading,
    heading,
    description,
    desktop_image,
    tablet_image,
    mobile_image,
    button_link,
    light_text
  } = block.full_image;

  const { srcSet: desktopSrcSet } = getContentImageProps({
    image: desktop_image.image,
    sizes: '(min-width: 1640px) 1640px, 100vw',
    fetchPriority: 'high',
    priority: true
  });
  const { srcSet: tabletSrcSet } = getContentImageProps({
    image: tablet_image.image,
    sizes: '100vw',
    fetchPriority: 'high',
    priority: true
  });
  const { srcSet: mobileSrcSet, remainingProps } = getContentImageProps({
    image: mobile_image.image,
    sizes: '100vw',
    fetchPriority: 'high',
    priority: true
  });

  const getSuperScriptString = (string) => {
    let str1;
    let str2;
    let superscript;
    if (string?.includes('®')) {
      str1 = string.split('®')[0];
      str2 = string.split('®')[1];
      superscript = '®';
    } else if (string?.includes('™')) {
      str1 = string.split('™')[0];
      str2 = string.split('™')[1];
      superscript = '™';
    } else if (string?.includes('©')) {
      str1 = string.split('©')[0];
      str2 = string.split('©')[1];
      superscript = '©';
    }

    return {
      str1: str1,
      str2: str2,
      superscript: superscript
    };
  };
  const preheadingStrings = getSuperScriptString(preheading);
  const headingStrings = getSuperScriptString(heading);
  const descriptionStrings = getSuperScriptString(description);

  const descriptionHtml = `<p>${descriptionStrings?.str1}<sup>${descriptionStrings?.superscript}</sup>${descriptionStrings?.str2}</p>`;
  const mobileOverlay = mobile_image.opacity_overlay;
  const tabletOverlay = tablet_image.opacity_overlay;
  const desktopOverlay = desktop_image.opacity_overlay;

  return (
    <section
      id={entry_uid + '_' + position}
      data-name="hero-content-full-image"
      className={`relative ${light_text ? 'text-white' : ''} mx-auto max-w-410 lg:min-h-132.5`}
    >
      <div className="max-w-410 lg:max-h-151 lg:min-h-132.5">
        <picture>
          <source media="(min-width: 1024px)" srcSet={desktopSrcSet} />
          <source media="(min-width: 641px)" srcSet={tabletSrcSet} />
          <source srcSet={mobileSrcSet} />
          <img
            {...remainingProps}
            className={`block min-h-135 w-full object-cover filter ${mobileOverlay ? 'brightness-75' : 'brightness-100'} ${tabletOverlay ? 'sm:brightness-75' : 'sm:brightness-100'} ${desktopOverlay ? 'lg:brightness-75' : 'lg:brightness-100'} sm:min-h-100 lg:min-h-132.5`}
            alt={resolveImageAltText(desktop_image.image)}
          />
        </picture>
        <div className="absolute top-0 flex h-full flex-col items-center justify-center px-5 sm:left-10 sm:max-w-2/5 sm:items-baseline sm:px-0 lg:left-20 xl:max-w-120">
          {preheading && (
            <p
              className={`preheading-r lg:preheading-l mb-2 flex flex-wrap lg:mb-3 ${light_text ? 'text-white' : 'text-fiery-cognac'}`}
            >
              {preheadingStrings?.superscript ? (
                <>
                  <span className="block">{preheadingStrings?.str1}</span>
                  <span className="body-s inline-block self-start pr-1">
                    {preheadingStrings?.superscript}
                  </span>
                  <span className="block">{preheadingStrings?.str2}</span>
                </>
              ) : (
                <>{preheading}</>
              )}
            </p>
          )}
          <h1 className="h3 lg:h1 flex flex-wrap text-center sm:text-left">
            {headingStrings?.superscript ? (
              <>
                <span className="block">{headingStrings?.str1}</span>
                <span className="body-s inline-block self-start pr-1">
                  {headingStrings?.superscript}
                </span>
                <span className="block">{headingStrings?.str2}</span>
              </>
            ) : (
              <>{heading}</>
            )}
          </h1>
          {description && (
            <>
              {descriptionStrings?.superscript ? (
                <div
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  className="body-r lg:body-l mt-3 flex flex-row flex-wrap text-center sm:text-left lg:mt-4"
                ></div>
              ) : (
                <p className="body-r lg:body-l mt-3 flex flex-row flex-wrap text-center sm:text-left lg:mt-4">
                  {description}
                </p>
              )}
            </>
          )}
          {button_link?.label && button_link.link_type == 'Internal Link' ? (
            <div>
              <Link
                href={`${button_link?.internal_link[0]?.url}${button_link?.query_string}${button_link?.anchor}`}
                prefetch={false}
                data-cy="sale-cta"
                className="btn btn-primary mt-5 inline-block lg:mt-8"
              >
                {button_link.label}
              </Link>
            </div>
          ) : button_link?.label ? (
            <div>
              <ExternalLink
                linkType={button_link?.link_type}
                linkStyling="btn btn-primary"
                externalLink={button_link?.external_link}
                linkText={button_link.label}
                dataCyAttribute="sale-cta"
              >
                {button_link.label}
              </ExternalLink>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
